import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Header from './components/Header';
import Footer from './components/Footer';
import LegalBar from './components/LegalBar';
import Analytics from './components/Analytics';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import PrivateRoute from './components/PrivateRoute';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home'; // eager: the landing page, kept in the main bundle for fast LCP
import './App.css';

// Everything below is code-split into its own chunk (React.lazy), so a
// storefront visitor no longer downloads the entire admin panel + every other
// page just to see the homepage. Each route's JS loads only when navigated to.
const Shop = lazy(() => import('./pages/Shop'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const Account = lazy(() => import('./pages/Account'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const Orders = lazy(() => import('./pages/Orders'));
const Brands = lazy(() => import('./pages/Brands'));
const Categories = lazy(() => import('./pages/Categories'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// Admin panel — a large, separate chunk that only admins ever load.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const OrdersManagement = lazy(() => import('./pages/OrdersManagement'));
const InventoryManagement = lazy(() => import('./pages/InventoryManagement'));
const AccountsManagement = lazy(() => import('./pages/AccountsManagement'));
const HRManagement = lazy(() => import('./pages/HRManagement'));
const ProductImageGenerator = lazy(() => import('./pages/ProductImageGenerator'));
const AdminImageLibrary = lazy(() => import('./pages/AdminImageLibrary'));
const AdminHeroSettings = lazy(() => import('./pages/AdminHeroSettings'));
const AdminSiteContent = lazy(() => import('./pages/AdminSiteContent'));
const AdminStyleTheLook = lazy(() => import('./pages/AdminStyleTheLook'));
const AdminWhatsApp = lazy(() => import('./pages/AdminWhatsApp'));
const AdminBlog = lazy(() => import('./pages/AdminBlog'));

// Storefront chrome (header/footer) is hidden on admin routes — the admin panel
// has its own top bar + sidebar.
function StoreChrome({ children }) {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  return children;
}

// Lightweight fallback while a route chunk loads.
function RouteFallback() {
  return <div style={{ minHeight: '60vh' }} />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <div className="app-container">
            <Analytics />
            <StoreChrome><VerifyEmailBanner /></StoreChrome>
            <StoreChrome><Header /></StoreChrome>
            <main className="main-content">
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/register" element={<Navigate to="/login" state={{ register: true }} replace />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/categories" element={<Categories />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/brands" element={<Brands />} />
                  <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />

                  {/* Protected Routes (User) */}
                  <Route
                    path="/account"
                    element={
                      <PrivateRoute>
                        <Account />
                      </PrivateRoute>
                    }
                  />
                  {/* Cart & checkout are open to guests (in-page sign-in prompt) */}
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route
                    path="/orders"
                    element={
                      <PrivateRoute>
                        <Orders />
                      </PrivateRoute>
                    }
                  />

                  {/* Admin Routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin-dashboard"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/orders"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <OrdersManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/inventory"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <InventoryManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/product-studio"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <ProductImageGenerator />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/accounts"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AccountsManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/hr"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <HRManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/image-library"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminImageLibrary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/hero-settings"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminHeroSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/site-content"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminSiteContent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/style-the-look"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminStyleTheLook />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/whatsapp"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminWhatsApp />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/blog"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminBlog />
                      </ProtectedRoute>
                    }
                  />

                  {/* 404 Page */}
                  <Route path="*" element={
                    <div className="not-found-container">
                      <h1>404 - Page Not Found</h1>
                      <p>The page you're looking for doesn't exist.</p>
                      <a href="/" className="btn btn-primary">Return to Home</a>
                    </div>
                  } />
                </Routes>
              </Suspense>
            </main>
            <StoreChrome><Footer /></StoreChrome>
            <StoreChrome><LegalBar /></StoreChrome>
          </div>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
